import { type PanelRow } from '../payload/panels'

/** A payload value as a number; null, undefined and junk read as 0. */
export const n = (v: unknown): number => Number(v) || 0

/** A payload value as text; null and undefined read as ''. */
export const s = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v)

export const num = (v: unknown): string =>
  v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US')

export const pct = (numerator: number, denominator: number): string =>
  denominator ? `${Math.round((numerator / denominator) * 100)}%` : '—'

/** "<1%", never "0%"; "~100%" for a rounded 100 that is not exactly 1. */
export const ratePct = (rate: number): string => {
  const pc = rate * 100
  if (pc > 0 && pc < 1) return '<1%'
  if (pc >= 100 && pc < 101 && rate !== 1) return '~100%'
  return `${Math.round(pc)}%`
}

export const plural = (count: number, one: string, many?: string): string =>
  `${num(count)} ${count === 1 ? one : (many ?? `${one}s`)}`

/** "a", "b" and "c" — for listing names inside a sentence. */
export const listOf = (names: string[]): string =>
  names.length < 2
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

/** Lowercases only the first letter, so proper nouns inside a label survive. */
export const lowerFirst = (text: string): string =>
  text ? text[0].toLowerCase() + text.slice(1) : text

export const day = (iso: unknown): string =>
  iso ? new Date(String(iso)).toISOString().slice(0, 10) : '—'

/** Whole UTC days between a timestamp and now: yesterday's import is 1, not 0 until 24h have passed. */
export const agoDays = (iso: string): number => {
  const utcDay = (ms: number): number => Math.floor(ms / 86_400_000)
  return utcDay(Date.now()) - utcDay(new Date(iso).getTime())
}

/** "Aug 24" — the same compact tick for days and weeks. */
export const shortDate = (value: unknown): string =>
  value
    ? new Date(`${String(value)}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : ''

/** The tooltip title for a bucket: the axis tick first, the calendar label after. */
export const bucketLabel = (
  row: PanelRow,
  xKey: string,
  grain: 'day' | 'week'
): string => {
  const value = row[xKey]
  if (!value) return ''
  const tick = shortDate(value)
  if (grain === 'day') {
    const weekday = row.weekday_name_short ? `${row.weekday_name_short}, ` : ''
    return row.week_label
      ? `${weekday}${tick}  ·  week ${row.week_label}`
      : `${weekday}${tick}`
  }
  return row.week_label
    ? `Week of ${tick}  ·  ${row.week_label}`
    : `Week of ${tick}`
}

export const monthRange = (from: unknown, to: unknown): string => {
  const fmt = (d: unknown): string =>
    new Date(`${String(d)}T00:00:00Z`).toLocaleDateString('en-GB', {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    })
  if (!from) return '—'
  return fmt(from) === fmt(to) ? fmt(from) : `${fmt(from)} – ${fmt(to)}`
}

export const biggest = <T extends PanelRow>(rows: T[], key: string): T | null =>
  rows.reduce<T | null>(
    (best, r) => (best === null || n(r[key]) > n(best[key]) ? r : best),
    null
  )

export const byKey = <T extends PanelRow>(
  rows: T[],
  key: string,
  value: unknown
): T | null => rows.find(r => r[key] === value) ?? null
